import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";

import { Footer, Header, PAGE_CONTAINER } from "../components/site";
import { SITE } from "../lib/seo-data";

export const Route = createFileRoute("/aanmelden")({
  head: () => ({
    meta: [
      { title: "Aanmelden als elektricien | offerto" },
      {
        name: "description",
        content:
          "Meld je elektrotechnisch bedrijf aan bij offerto en ontvang offerteaanvragen van huiseigenaren in jouw regio.",
      },
      { property: "og:title", content: "Aanmelden als elektricien | offerto" },
      {
        property: "og:description",
        content:
          "Meld je elektrotechnisch bedrijf aan en ontvang offerteaanvragen van huiseigenaren in jouw regio.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}/aanmelden` },
      { property: "og:locale", content: "nl_NL" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/aanmelden` }],
  }),
  component: Aanmelden,
});

const inputClass =
  "flex h-12 w-full rounded-md border border-input bg-background px-4 py-2 text-base text-foreground shadow-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary md:text-sm";

const labelClass = "text-sm font-medium leading-none";

const specialisaties = [
  "Algemene elektrotechniek",
  "Groepenkasten en meterkasten",
  "Laadpalen en laadstations",
  "Verlichting",
  "Databekabeling en netwerken",
  "Storingen en spoed",
  "Zonnepanelen en thuisbatterijen",
  "Witgoed en reparatie",
];

function Aanmelden() {
  const [sent, setSent] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSent(true);
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      {/* Mobiel uitklapmenu: snel naar offertewizard en prijslijst */}
      <div className="border-b border-border/60 bg-background lg:hidden">
        <div className={`${PAGE_CONTAINER} py-3`}>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-label={menuOpen ? "Menu sluiten" : "Menu openen"}
            className="flex w-full items-center justify-between rounded-lg border border-border px-4 py-2.5 text-sm font-semibold text-foreground"
          >
            Offerte & prijzen
            <svg
              viewBox="0 0 24 24"
              className={`h-4 w-4 transition-transform ${menuOpen ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {menuOpen && (
            <ul className="mt-2 flex flex-col gap-1 text-sm font-medium">
              <li>
                <Link
                  to="/aanvraag"
                  onClick={() => setMenuOpen(false)}
                  className="block rounded-lg px-3 py-2.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
                >
                  Offerte aanvragen
                </Link>
              </li>
              <li>
                <Link
                  to="/diensten"
                  onClick={() => setMenuOpen(false)}
                  className="block rounded-lg px-3 py-2.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
                >
                  Prijslijst bekijken
                </Link>
              </li>
            </ul>
          )}
        </div>
      </div>
      <main className="w-full">
        <section className="bg-secondary">
          <div className={`${PAGE_CONTAINER} py-14`}>
            <span className="inline-flex rounded-xl bg-foreground px-4 py-2 text-xs font-extrabold uppercase tracking-wider text-background">
              VOOR ELEKTRICIENS
            </span>
            <h1 className="mt-5 max-w-[90%] text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
              Aanmelden als elektricien
            </h1>
            <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
              Vul je bedrijfsgegevens in. We reviewen je aanmelding en nemen contact met je op.
            </p>
          </div>
        </section>

        <div className={`${PAGE_CONTAINER} py-14`}>
          {sent ? (
            <div className="mx-auto w-full max-w-4xl rounded-xl border border-primary/30 bg-primary/10 p-6 sm:p-8">
              <p className="font-semibold">Bedankt voor je aanmelding.</p>
              <p className="mt-1 text-sm text-muted-foreground">
                We reviewen je aanmelding en nemen zo snel mogelijk contact met je op.
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="mx-auto w-full max-w-4xl rounded-xl border border-border bg-card p-6 sm:p-8"
            >
              <div className="grid gap-6 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <label className={labelClass} htmlFor="bedrijfsnaam">
                    Bedrijfsnaam *
                  </label>
                  <input
                    className={inputClass}
                    id="bedrijfsnaam"
                    name="bedrijfsnaam"
                    required
                    maxLength={120}
                    placeholder="Bijv. Van Dijk Elektrotechniek B.V."
                  />
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <label className={labelClass} htmlFor="contactpersoon">
                    Contactpersoon *
                  </label>
                  <input
                    className={inputClass}
                    id="contactpersoon"
                    name="contactpersoon"
                    required
                    maxLength={120}
                    placeholder="Naam van de contactpersoon"
                  />
                </div>

                <div className="space-y-2">
                  <label className={labelClass} htmlFor="email">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    className={inputClass}
                    id="email"
                    name="email"
                    required
                    maxLength={254}
                    placeholder="info@bedrijf.nl"
                  />
                </div>

                <div className="space-y-2">
                  <label className={labelClass} htmlFor="telefoon">
                    Telefoon *
                  </label>
                  <input
                    type="tel"
                    className={inputClass}
                    id="telefoon"
                    name="telefoon"
                    required
                    minLength={8}
                    maxLength={30}
                    placeholder="06 12345678"
                  />
                </div>

                <div className="space-y-2">
                  <label className={labelClass} htmlFor="kvk_nummer">
                    KvK-nummer *
                  </label>
                  <input
                    className={inputClass}
                    id="kvk_nummer"
                    name="kvk_nummer"
                    required
                    minLength={8}
                    maxLength={20}
                    placeholder="12345678"
                  />
                </div>

                <div className="space-y-2">
                  <label className={labelClass} htmlFor="btw_nummer">
                    BTW-nummer
                  </label>
                  <input
                    className={inputClass}
                    id="btw_nummer"
                    name="btw_nummer"
                    maxLength={20}
                    placeholder="NL123456789B01"
                  />
                </div>

                <div className="space-y-2">
                  <label className={labelClass} htmlFor="iban">
                    IBAN
                  </label>
                  <input
                    className={inputClass}
                    id="iban"
                    name="iban"
                    maxLength={34}
                    placeholder="NL00BANK0123456789"
                  />
                </div>

                <div className="space-y-2">
                  <label className={labelClass} htmlFor="specialisatie">
                    Specialisatie
                  </label>
                  <select
                    id="specialisatie"
                    name="specialisatie"
                    defaultValue=""
                    className={`${inputClass} appearance-none pr-10`}
                  >
                    <option value="" disabled>
                      Kies een specialisatie
                    </option>
                    {specialisaties.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <label className={labelClass} htmlFor="straat">
                    Straat *
                  </label>
                  <input
                    className={inputClass}
                    id="straat"
                    name="straat"
                    required
                    maxLength={120}
                    placeholder="Straatnaam"
                  />
                </div>

                <div className="space-y-2">
                  <label className={labelClass} htmlFor="huisnummer">
                    Huisnummer *
                  </label>
                  <input
                    className={inputClass}
                    id="huisnummer"
                    name="huisnummer"
                    required
                    maxLength={20}
                    placeholder="1"
                  />
                </div>

                <div className="space-y-2">
                  <label className={labelClass} htmlFor="postcode">
                    Postcode *
                  </label>
                  <input
                    className={inputClass}
                    id="postcode"
                    name="postcode"
                    required
                    minLength={4}
                    maxLength={12}
                    placeholder="1234 AB"
                  />
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <label className={labelClass} htmlFor="plaats">
                    Plaats *
                  </label>
                  <input
                    className={inputClass}
                    id="plaats"
                    name="plaats"
                    required
                    maxLength={120}
                    placeholder="Plaats"
                  />
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <label className={labelClass} htmlFor="werkgebied">
                    Werkgebied
                  </label>
                  <input
                    className={inputClass}
                    id="werkgebied"
                    name="werkgebied"
                    maxLength={120}
                    placeholder="Bijv. heel Nederland, provincie of regio"
                  />
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <label className={labelClass} htmlFor="kvk_extract">
                    Kopie KvK-uittreksel *
                  </label>
                  <input
                    type="file"
                    className="flex h-12 w-full rounded-md border border-input bg-background px-4 py-2 text-base text-foreground shadow-sm outline-none transition-colors file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-primary-foreground placeholder:text-muted-foreground hover:file:bg-primary/85 focus:border-primary md:text-sm"
                    id="kvk_extract"
                    name="kvk_extract"
                    accept=".pdf,.jpg,.jpeg,.png"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Maximaal 5 MB. Toegestane bestanden: PDF, JPG of PNG.
                  </p>
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <label className={labelClass} htmlFor="bericht">
                    Optioneel bericht
                  </label>
                  <textarea
                    className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-4 py-3 text-base text-foreground shadow-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary md:text-sm"
                    id="bericht"
                    name="bericht"
                    maxLength={2000}
                    rows={4}
                    placeholder="Vertel kort waarom je wilt aansluiten..."
                  />
                </div>

                <div className="flex items-start gap-3 sm:col-span-2">
                  <input
                    type="checkbox"
                    id="toestemming"
                    name="toestemming"
                    required
                    className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded-sm border border-primary accent-[#0F172A]"
                  />
                  <label
                    className="text-sm font-normal leading-tight text-muted-foreground"
                    htmlFor="toestemming"
                  >
                    Ik geef toestemming om mijn bedrijfsgegevens te verwerken en contact met mij op
                    te nemen over de samenwerking. *
                  </label>
                </div>
              </div>

              <div className="mt-8">
                <button
                  className="inline-flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary px-8 py-2 text-base font-bold text-primary-foreground shadow-sm transition-colors hover:bg-primary/85 disabled:cursor-not-allowed disabled:opacity-60"
                  type="submit"
                >
                  Aanmelding indienen
                </button>
              </div>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
